import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ChevronRightIcon, XIcon } from "@heroicons/react/outline";
import { classNames } from "../../helpers";

type modalProps = {
  children?: any | React.ReactNode;
  setOpen: any;
  open: boolean;
  className?: string;
};
export default function Modal({
  children,
  setOpen,
  open,
  className,
}: modalProps) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-800 bg-opacity-50 transition-opacity" />
          </Transition.Child>
          <div className="pointer-events-none fixed inset-y-0 end-0 flex max-w-full sm:ps-10">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300 sm:duration-500"
              enterFrom="ltr:translate-x-full rtl:-translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300 sm:duration-500"
              leaveFrom="translate-x-0"
              leaveTo="ltr:translate-x-full rtl:-translate-x-full"
            >
              <div
                className={classNames(
                  "pointer-events-auto w-screen",
                  !!className ? className : " max-w-[37.5rem]"
                )}
              >
                <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                  {children}
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

Modal.Footer = ({ children }: { children?: any | React.ReactNode }) => {
  return (
    <div className="flex flex-shrink-0 justify-end px-4 py-4">{children}</div>
  );
};
Modal.Body = ({
  children,
  title,
  setOpen,
  withBackOnMobile = false,
  className,
}: {
  children?: any | React.ReactNode;
  title?: string;
  setOpen: any;
  withBackOnMobile?: boolean;
  className?: string;
}) => {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-6">
      <div className={classNames("px-4 sm:px-6", !!className ? className : "")}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 text-gray-900">
            <button
              type="button"
              data-cy="closeModel"
              className={classNames(
                "rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brandblue-500",
                withBackOnMobile ? "block sm:hidden" : "hidden"
              )}
              onClick={() => setOpen(false)}
            >
              <span className="sr-only">Close panel</span>
              <ChevronRightIcon className="w-6 h-6 ltr:rotate-180" />
            </button>
            <Dialog.Title className="text-lg font-medium">{title}</Dialog.Title>
          </div>
          <div
            className={classNames(
              "ms-3 h-7 items-center",
              withBackOnMobile ? "hidden sm:flex" : "flex"
            )}
          >
            <button
              type="button"
              data-cy="closeModel"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandblue-500"
              onClick={() => setOpen(false)}
            >
              <span className="sr-only">Close panel</span>
              <XIcon
                data-cy="closeIcon"
                className="h-6 w-6"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
      <div className="relative mt-6 flex-1 px-4 sm:px-6">{children}</div>
    </div>
  );
};
